import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../api';
import { EyeOff, MessageCircle, AlertTriangle, Eye } from 'lucide-react';
import ModeratorNavbar from './ModeratorNavbar';
import './ModeratorDashboard.css';

const ModeratorReports = ({ onLogout }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasAccess, setHasAccess] = useState(true);
  const [reportsConfig, setReportsConfig] = useState(null);
  const [filters, setFilters] = useState({
    status: 'active',
    type: 'all',
    search: ''
  });
  
  // Modal states
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null); // Track the report context for messaging
  const [sending, setSending] = useState(false);
  
  // Use refs for text inputs to avoid re-renders on every keystroke
  const hideReasonRef = useRef(null);
  const messageTextRef = useRef(null);
  const disableMessageRef = useRef(null);

  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const allowed = !!(
        user && (user.role === 'admin' || (user.role === 'moderator' && (user.moderatorActive || user.moderator)))
      );
      setHasAccess(allowed);
      if (!allowed) {
        setError('Access denied. Moderator privileges required.');
        return;
      }
    } catch (e) {
      setHasAccess(false);
      setError('Access denied. Moderator privileges required.');
      return;
    }
    loadReports();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/reports/config');
        if (!cancelled) setReportsConfig(res.data || null);
      } catch (e) {
        if (!cancelled) setReportsConfig(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const severityFilterOptions = useMemo(() => {
    const rows = reportsConfig?.severityTypes;
    if (Array.isArray(rows) && rows.length) {
      return rows
        .filter(r => r && r.type)
        .slice()
        .sort((a, b) => (Number(a.severityRank) || 0) - (Number(b.severityRank) || 0));
    }
    return [
      { type: 'level_1', severityRank: 1, label: 'Level 1' },
      { type: 'level_2', severityRank: 2, label: 'Level 2' },
      { type: 'level_3', severityRank: 3, label: 'Level 3' }
    ];
  }, [reportsConfig]);

  const loadReports = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await api.get('/api/reports/all?limit=500');
      setReports(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleHidePost = async (postId) => {
    const reason = hideReasonRef.current?.value?.trim();
    if (!reason) {
      alert('Please provide a reason for hiding this post');
      return;
    }

    setSending(true);
    try {
      await api.post(`/api/moderator/post/${postId}/hide`, { reason });
      alert('Post hidden successfully');
      setSelectedPost(null);
      if (hideReasonRef.current) hideReasonRef.current.value = '';
      loadReports();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to hide post');
    } finally {
      setSending(false);
    }
  };

  const handleUnhidePost = async (postId) => {
    try {
      await api.post(`/api/moderator/post/${postId}/unhide`);
      alert('Post unhidden successfully');
      loadReports();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to unhide post');
    }
  };

  const handleHideUserPosts = async (userId) => {
    const reason = prompt('Enter reason for hiding all posts by this user:');
    if (!reason) return;

    try {
      await api.post(`/api/moderator/user/${userId}/hide-all-posts`, { reason });
      alert('All user posts hidden successfully');
      loadReports();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to hide user posts');
    }
  };

  const handleSendMessage = async (userId) => {
    const msgText = messageTextRef.current?.value?.trim();
    if (!msgText) {
      alert('Please provide a message');
      return;
    }
    
    if (!userId) {
      alert('No user ID available for this report');
      return;
    }

    setSending(true);
    try {
      // Include report context if available
      const payload = { 
        message: msgText,
        requestType: 'info'
      };
      
      if (selectedReport) {
        payload.reportId = selectedReport.id;
        payload.reportDescription = selectedReport.description?.substring(0, 100);
        payload.reportType = selectedReport.type;
      }
      
      await api.post(`/api/moderator/message/${userId}`, payload);
      alert('Message sent successfully');
      setSelectedUserId(null);
      setSelectedReport(null);
      if (messageTextRef.current) messageTextRef.current.value = '';
      loadReports();
    } catch (err) {
      console.error('Error sending message:', err);
      alert(err.response?.data?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleDisableUser = async (userId) => {
    const disableMsg = disableMessageRef.current?.value?.trim();
    if (!disableMsg) {
      alert('Please provide a message for the user');
      return;
    }

    setSending(true);
    try {
      await api.post(`/api/moderator/user/${userId}/disable`, { 
        customMessage: disableMsg 
      });
      alert('User disabled successfully');
      setSelectedUserId(null);
      if (disableMessageRef.current) disableMessageRef.current.value = '';
      loadReports();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to disable user');
    } finally {
      setSending(false);
    }
  };

  const filteredReports = useMemo(() => {
    return reports.filter(report => {
      if (filters.status === 'active' && report.hiddenByModerator) return false;
      if (filters.status === 'hidden' && !report.hiddenByModerator) return false;
      if (filters.type !== 'all' && report.type !== filters.type) return false;
      if (filters.search) {
        const text = `${report.description || ''} ${report.location || ''} ${report.address || ''}`.toLowerCase();
        if (!text.includes(filters.search.toLowerCase().trim())) return false;
      }
      return true;
    });
  }, [reports, filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({ status: 'active', type: 'all', search: '' });
  };

  const formatTypeLabel = (type) => {
    if (!type) return 'Unknown';
    return type.replace(/_/g, ' ').replace('level', 'Level ');
  };

  return (
    <div className="moderator-shell">
      <ModeratorNavbar onLogout={onLogout} />

      {error && <div className="mod-alert">{error}</div>}

      {!hasAccess ? (
        <div className="moderator-content">
          <div className="mod-panel">
            <div className="loading">Access denied. Moderator privileges required.</div>
          </div>
        </div>
      ) : (
        <div className="moderator-content">
          {loading ? (
            <div className="loading">Loading reports...</div>
          ) : (
            <div className="mod-panel">
              <div className="mod-filter-bar">
                <div className="filter-field">
                  <label>Status</label>
                  <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
                    <option value="active">Active</option>
                    <option value="hidden">Hidden</option>
                    <option value="all">All</option>
                  </select>
                </div>
                <div className="filter-field">
                  <label>Severity</label>
                  <select value={filters.type} onChange={(e) => handleFilterChange('type', e.target.value)}>
                    <option value="all">All</option>
                    {severityFilterOptions.map(opt => (
                      <option key={opt.type} value={opt.type}>
                        {opt.label || opt.type}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="filter-field search">
                  <label>Search</label>
                  <input
                    type="text"
                    placeholder="Description or location"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                </div>
                <button className="filter-clear" onClick={clearFilters}>Reset</button>
              </div>

              <div className="mod-table-wrapper">
                <table className="mod-table">
                  <thead>
                    <tr>
                      <th>User ID</th>
                      <th>Severity</th>
                      <th>Description</th>
                      <th>Address</th>
                      <th>Upvotes</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReports.length === 0 && (
                      <tr>
                        <td colSpan="8" className="empty-row">
                          No reports match the current filters.
                        </td>
                      </tr>
                    )}
                    {filteredReports.map(report => (
                      <tr key={report.id}>
                        <td>
                          <code style={{fontSize: '0.75rem', background: '#f0f0f0', padding: '2px 6px', borderRadius: '4px'}}>
                            {report.createdBy ? report.createdBy.substring(0, 12) + '...' : 'Anonymous'}
                          </code>
                        </td>
                        <td>
                          <span className={`level-pill ${report.type}`}>
                            {formatTypeLabel(report.type)}
                          </span>
                        </td>
                        <td>
                          <div className="cell-description">
                            <p>{report.description || 'No description provided'}</p>
                            <span className="cell-meta">
                              Reporter: {report.userName || 'Anonymous'} • Comments: {report.comments?.length || 0}
                            </span>
                          </div>
                        </td>
                        <td>
                          {report.address ? (
                            <div>
                              <div>{report.address}</div>
                              {report.position?.lat && report.position?.lng && (
                                <a
                                  href={`https://www.google.com/maps?q=${report.position.lat},${report.position.lng}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ fontSize: '12px', color: '#0d6efd', textDecoration: 'none' }}
                                >
                                  {report.position.lat.toFixed(4)}, {report.position.lng.toFixed(4)}
                                </a>
                              )}
                            </div>
                          ) : (
                            report.position?.lat && report.position?.lng ? (
                              <a
                                href={`https://www.google.com/maps?q=${report.position.lat},${report.position.lng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ fontSize: '12px', color: '#0d6efd', textDecoration: 'none' }}
                              >
                                {report.position.lat.toFixed(4)}, {report.position.lng.toFixed(4)}
                              </a>
                            ) : (
                              <span>Not provided</span>
                            )
                          )}
                        </td>
                        <td>{report.upvoteCount || 0}</td>
                        <td>{new Date(report.timestamp).toLocaleDateString()}</td>
                        <td>
                          <span className={`status-pill ${report.hiddenByModerator ? 'hidden' : 'active'}`}>
                            {report.hiddenByModerator ? 'Hidden' : 'Active'}
                          </span>
                        </td>
                        <td>
                          <div className="table-actions">
                            {report.hiddenByModerator ? (
                              <button className="icon-btn success" onClick={() => handleUnhidePost(report.id)}>
                                <Eye size={14} /> Unhide
                              </button>
                            ) : (
                              <button className="icon-btn warning" onClick={() => setSelectedPost(report)}>
                                <EyeOff size={14} /> Hide
                              </button>
                            )}
                            <button className="icon-btn neutral" onClick={() => {
                              const userId = report.createdBy || report.userId;
                              if (userId) {
                                setSelectedUserId(userId);
                                setSelectedReport(report);
                              } else {
                                alert('Cannot message anonymous user - no user ID available');
                              }
                            }}>
                              <MessageCircle size={14} /> Message
                            </button>
                            <button className="icon-btn danger" onClick={() => handleHideUserPosts(report.createdBy || report.userId)}>
                              <AlertTriangle size={14} /> Hide User
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {hasAccess && (
        <>
      {/* Hide Post Modal */}
      {selectedPost && (
        <div className="modal-overlay" onClick={() => setSelectedPost(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Hide Post</h3>
            <p>Provide a reason for hiding this post:</p>
            <textarea
              ref={hideReasonRef}
              defaultValue=""
              placeholder="Enter reason..."
              rows={4}
            />
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setSelectedPost(null)}>
                Cancel
              </button>
              <button 
                className="btn btn-warning" 
                onClick={() => handleHidePost(selectedPost.id)}
                disabled={sending}
              >
                {sending ? 'Hiding...' : 'Hide Post'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message User Modal */}
      {selectedUserId && (
        <div className="modal-overlay" onClick={() => { setSelectedUserId(null); setSelectedReport(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Send Message to User</h3>
            {selectedReport && (
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '10px',
                borderRadius: '6px',
                marginBottom: '15px',
                fontSize: '13px',
                borderLeft: '3px solid #6c757d'
              }}>
                <strong>Regarding Report:</strong><br/>
                <span style={{ color: '#666' }}>
                  {selectedReport.type} - {selectedReport.description?.substring(0, 80)}{selectedReport.description?.length > 80 ? '...' : ''}
                </span>
              </div>
            )}
            <textarea
              ref={messageTextRef}
              defaultValue=""
              placeholder="Enter your message..."
              rows={4}
            />
            <div className="form-group">
              <label>Or Disable User:</label>
              <textarea
                ref={disableMessageRef}
                defaultValue=""
                placeholder="Custom message for disabled user..."
                rows={3}
              />
              <button 
                className="btn btn-danger" 
                onClick={() => handleDisableUser(selectedUserId)}
                disabled={sending}
              >
                {sending ? 'Disabling...' : 'Disable User'}
              </button>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setSelectedUserId(null)}>
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={() => handleSendMessage(selectedUserId)}
                disabled={sending}
              >
                {sending ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>
      )}

        </>
      )}
    </div>
  );
};

export default ModeratorReports;
